import { hardcodedReminderOffsets } from '@shared/core/custom-offsets'
import { MESSAGE_TEMPLATE_VARCHAR_MAX } from '@shared/core/message-templates'
import { MessageSquareMore } from 'lucide-react'
import Tooltip from 'rc-tooltip'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import type { FieldError } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { AppointmentConfirmationStepsModal } from 'src/modules/confirmation/containers'
import {
  Button,
  FormControl,
  Icon,
  SelectField,
  TextField,
  ToggleSwitch,
  Typography,
} from 'src/modules/core/components'
import { MessageInput } from 'src/modules/core/components/message-input/message-input'
import { buildMessageFieldPlaceholder } from 'src/modules/reminder/utils/dynamic-data-token-hints.utils'
import { generateEstimatedEventMessage } from 'src/modules/event/utils/message-builder'
import {
  useMessageTemplates,
  useUpdateMessageTemplate,
} from 'src/modules/message-templates/hooks/use-message-templates'
import { useUserPreferences } from 'src/modules/preferences/hooks/use-user-preferences'
import { useAppointmentTypes, useUpdateAppointmentType } from 'src/modules/user/hooks/use-appointment-types'
import { useUpdateBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useMessageLanguage } from 'src/modules/user/hooks/use-message-language'
import { useOptimisticUpdateProfile } from 'src/modules/user/hooks/use-optimistic-update-profile'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { getBookingReminderSettingsDefaultValues } from 'src/modules/user/utils/onboarding-template-defaults'
import classes from './booking-reminder-settings-onboarding.module.scss'
import {
  getBookingReminderSettingsResolver,
  type ReminderSettingsFormData,
} from './booking-reminder-settings-onboarding.schema'

export function BookingReminderSettingsOnboarding() {
  const { formatMessage } = useIntl()
  const profile = useProfile()
  const { language: messageLanguage } = useMessageLanguage(profile.data?.default_message_template?.language)
  const preferences = useUserPreferences()
  const messageTemplates = useMessageTemplates()
  const updateMessageTemplate = useUpdateMessageTemplate()
  const optimisticUpdateProfile = useOptimisticUpdateProfile()
  const updateBookingOnboarding = useUpdateBookingOnboarding()
  const appointmentTypes = useAppointmentTypes(profile.data?.id || '', {
    enabled: !!profile.data?.id,
  })
  const updateAppointmentType = useUpdateAppointmentType()

  const [isAppointmentConfirmationModalOpen, setIsAppointmentConfirmationModalOpen] = useState(false)

  // Find default message template
  const defaultTemplate = useMemo(() => {
    return messageTemplates.data?.find((template) => template.is_default)
  }, [messageTemplates.data])

  const reminderPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('reminder', messageLanguage),
    [messageLanguage],
  )

  const defaultValues = useMemo(
    () => getBookingReminderSettingsDefaultValues(profile.data, defaultTemplate),
    [profile.data, defaultTemplate],
  )

  const hasTemplateSource = Boolean(defaultTemplate || profile.data?.default_message_template)

  const resolver = useMemo(
    () => getBookingReminderSettingsResolver(profile.data, preferences.data),
    [profile.data, preferences.data],
  )

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ReminderSettingsFormData>({
    mode: 'onChange',
    resolver,
    defaultValues,
  })

  // Reset form when default values change (e.g., when template loads)
  useEffect(() => {
    reset(defaultValues)
    void trigger()
  }, [defaultValues, reset, trigger])

  const { reminderMessage, location, from, appointmentConfirmation } = watch()

  const {
    message: messagePreview,
    messageLength,
    segmentsCount,
  } = useMemo(() => {
    return generateEstimatedEventMessage(
      'reminder',
      {
        ...profile.data,
        appointment_confirmation: appointmentConfirmation,
        default_message_template: {
          ...profile.data.default_message_template,
          sender_name: from,
          location: location,
          reminder_message: reminderMessage,
        },
      },
      preferences.data,
      profile.data?.default_message_template?.language,
    )
  }, [profile.data, preferences.data, reminderMessage, from, location, appointmentConfirmation])

  const isLoading =
    updateMessageTemplate.isLoading ||
    optimisticUpdateProfile.isLoading ||
    updateBookingOnboarding.isLoading ||
    updateAppointmentType.isLoading ||
    profile.isLoading ||
    messageTemplates.isLoading ||
    preferences.isLoading ||
    appointmentTypes.isLoading

  const onSubmit = async (data: ReminderSettingsFormData) => {
    if (!defaultTemplate || !profile.data) {
      return
    }

    try {
      // Update message template fields
      await updateMessageTemplate.mutateAsync({
        id: defaultTemplate.id,
        dto: {
          sender_name: data.from,
          location: data.location,
          reminder_message: data.reminderMessage,
          reminder_offsets: data.timing.map((t) => +t),
        },
      })

      // Update appointment confirmation in profile
      await optimisticUpdateProfile.mutateAsync({
        appointment_confirmation: Boolean(data.appointmentConfirmation),
      })

      // Get the only appointment type (created during onboarding)
      const appointmentTypeToUpdate = appointmentTypes.data?.[0]
      if (appointmentTypeToUpdate) {
        await updateAppointmentType.mutateAsync({
          id: appointmentTypeToUpdate.id,
          data: {
            set_reminders_using_template_id: data.sendReminders ? defaultTemplate.id : null,
          },
        })
      }

      // Mark reminder settings step as complete
      await updateBookingOnboarding.mutateAsync({
        reminder_settings_set: true,
      })

      // The wrapper will automatically advance to the next step when bookingOnboarding updates
    } catch (error) {
      console.error('Failed to update reminder settings:', error)
    }
  }

  if (isLoading || !profile.data || !preferences.data || !hasTemplateSource) {
    return <div className={classes.onboardingSection}>Loading...</div>
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.onboardingSection}>
        <Typography variant="h5" className={classes.title}>
          <MessageSquareMore />
          SMS Appointment Reminders
        </Typography>
        <Typography variant="body" className={classes.subtitle}>
          Reduce no-shows by reminding customers about their appointments ahead of time
        </Typography>

        {/* Reminder Toggle */}
        <FormControl error={undefined} className={classes.fieldWrapper}>
          <div className={classes.reminderToggleWrapper}>
            <div className={classes.reminderToggleContent}>
              <span className={classes.recommendedPill}>Recommended</span>
              <Typography variant="body" className={classes.reminderLabel}>
                Automatically send appointment reminders to customers
              </Typography>
            </div>
            <Controller
              name="sendReminders"
              control={control}
              render={({ field }) => <ToggleSwitch checked={field.value} onChange={field.onChange} />}
            />
          </div>
        </FormControl>

        <hr className={classes.divider} />

        <TextField
          wrapperClassName={classes.fieldWrapper}
          label="Business Name"
          placeholder={formatMessage({
            id: 'configuration.step1.placeholder.from',
          })}
          maxLength={MESSAGE_TEMPLATE_VARCHAR_MAX}
          {...register('from')}
          error={errors.from?.message}
          labelAddOn={
            <Tooltip
              placement="top"
              mouseEnterDelay={0.1}
              overlayClassName={classes.tooltipOverlay}
              align={{ offset: [0, -20] }}
              overlay={
                <Typography variant="body" className={classes.tooltipText}>
                  Use {'{from}'} in your message template, and it will be replaced automatically with your business name
                  when messages are sent.
                </Typography>
              }
            >
              <span className={classes.textFieldAddOnPill}>{'{from}'}</span>
            </Tooltip>
          }
        />

        <TextField
          wrapperClassName={classes.fieldWrapper}
          label={formatMessage({
            id: 'configuration.step1.label.location',
          })}
          Element="textarea"
          rows={3}
          placeholder={formatMessage({
            id: 'configuration.step1.placeholder.location',
          })}
          maxLength={MESSAGE_TEMPLATE_VARCHAR_MAX}
          {...register('location')}
          error={errors.location?.message}
          labelAddOn={
            <Tooltip
              placement="top"
              mouseEnterDelay={0.1}
              overlayClassName={classes.tooltipOverlay}
              align={{ offset: [0, -20] }}
              overlay={
                <Typography variant="body" className={classes.tooltipText}>
                  Use {'{location}'} in your message template, and it will be replaced automatically with this location
                  or link when messages are sent.
                </Typography>
              }
            >
              <span className={classes.textFieldAddOnPill}>{'{location}'}</span>
            </Tooltip>
          }
        />

        <MessageInput
          wrapperClassName={classes.fieldWrapper}
          label={formatMessage({
            id: 'configuration.step1.label.reminder',
          })}
          placeholder={reminderPlaceholder}
          rows={3}
          {...register('reminderMessage')}
          error={errors.reminderMessage?.message}
          showDynamicDataHelper
          messageLanguage={messageLanguage}
          confirmationsEnabled={appointmentConfirmation}
          messagePreview={messagePreview}
          messageTemplate={reminderMessage}
          messageLength={messageLength}
          segmentsCount={segmentsCount}
        />

        <FormControl
          label={formatMessage({ id: 'configuration.step1.timing.title' })}
          error={(errors.timing as unknown as FieldError | undefined)?.message ?? errors.timing?.[0]?.message}
          className={classes.fieldWrapper}
        >
          <Controller
            control={control}
            name="timing"
            render={({ field: { onChange, value, ref } }) => (
              <SelectField
                ref={ref}
                value={hardcodedReminderOffsets.filter((c) => value.includes(c.value))}
                onChange={(val) => onChange(val.map((c) => c.value))}
                options={hardcodedReminderOffsets}
                isMulti
              />
            )}
          />
        </FormControl>

        <FormControl error={errors?.appointmentConfirmation?.message} className={classes.fieldWrapper}>
          <div className={classes.reminderToggleWrapper}>
            <div className={classes.reminderToggleContent}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography variant="body" className={classes.reminderLabel}>
                  {formatMessage({
                    id: 'settings.appointment.confirmation',
                  })}
                </Typography>
                <Icon
                  name="questionMark"
                  className={classes.tooltipLink}
                  onClick={() => setIsAppointmentConfirmationModalOpen(true)}
                />
              </div>
              <Typography variant="body" className={classes.infoText}>
                Allow customers to confirm or cancel appointments from your reminders
              </Typography>
            </div>
            <Controller
              control={control}
              name="appointmentConfirmation"
              render={({ field: { onChange, value, ref } }) => (
                <ToggleSwitch
                  ref={ref}
                  checked={value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
          </div>
        </FormControl>

        <div className={classes.formActions}>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || !defaultTemplate}
            className={classes.continueButton}
          >
            {isLoading ? 'Saving...' : 'Finish & Get My Booking Page'}
          </Button>
        </div>
      </form>

      {isAppointmentConfirmationModalOpen && (
        <AppointmentConfirmationStepsModal onClose={() => setIsAppointmentConfirmationModalOpen(false)} />
      )}
    </>
  )
}
